import React, { useState, useEffect } from "react";
import { Show } from "@refinedev/antd";
import { useShow, useNavigation } from "@refinedev/core";
import { 
  Typography, 
  Card, 
  Row, 
  Col, 
  Descriptions, 
  Table, 
  Tabs, 
  Tag, 
  Space,
  Button,
  Statistic,
  Timeline,
  Avatar,
  Tooltip
} from "antd";
import { 
  UserOutlined, 
  PhoneOutlined, 
  MailOutlined, 
  HomeOutlined,
  CalendarOutlined,
  FileTextOutlined,
  EuroOutlined,
  DownloadOutlined,
  EditOutlined,
  ReloadOutlined,
  UnorderedListOutlined
} from "@ant-design/icons";
import { IGuest, IBooking, IDocument } from "../../interfaces";
import { useTypo3Api } from "../../services/typo3Service";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

export const GuestShow: React.FC = () => {
  const { queryResult } = useShow<IGuest>();
  const guest = queryResult?.data?.data;
  const { list, edit } = useNavigation();
  
  const { 
    getBookingsForGuest, 
    getInvoicesForGuest, 
    getOffersForGuest,
    getRatingsForGuest,
    getGuestDetailStatistics,
    downloadInvoicePDF 
  } = useTypo3Api();
  
  const [bookings, setBookings] = useState<IBooking[]>([]);
  const [invoices, setInvoices] = useState<IDocument[]>([]);
  const [offers, setOffers] = useState<IDocument[]>([]);
  const [ratings, setRatings] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (guest?.id) {
      loadGuestData();
    }
  }, [guest?.id]);

  // Robuste Daten-Sanitization
  const sanitizeTableData = (data: any[], dataType: string) => {
    if (!Array.isArray(data)) {
      console.warn(`⚠️ ${dataType} ist kein Array:`, data);
      return [];
    }
    
    return data.map((item, index) => {
      if (!item || typeof item !== 'object') {
        console.warn(`⚠️ ${dataType}[${index}] ist kein Objekt:`, item);
        return { id: `error-${index}`, error: true, ...item };
      }
      
      // Sanitize verschachtelte Objekte
      const sanitizedItem = { ...item };
      
      // Advert-Objekt validieren und reparieren
      if (sanitizedItem.advert && typeof sanitizedItem.advert === 'object') {
        sanitizedItem.advert = {
          id: sanitizedItem.advert.id || null,
          title: sanitizedItem.advert.title || 'Unbekannte Unterkunft'
        };
      } else {
        sanitizedItem.advert = {
          id: null,
          title: 'Unbekannte Unterkunft'
        };
      }
      
      // Numerische Felder validieren
      sanitizedItem.adults = parseInt(sanitizedItem.adults) || 0;
      sanitizedItem.children = parseInt(sanitizedItem.children) || 0;
      sanitizedItem.dogs = parseInt(sanitizedItem.dogs) || 0;
      sanitizedItem.nights = parseInt(sanitizedItem.nights) || 0;
      
      // Datum-Felder validieren
      if (!sanitizedItem.start_date || !sanitizedItem.end_date) {
        sanitizedItem.start_date = sanitizedItem.start_date || new Date().toISOString().split('T')[0];
        sanitizedItem.end_date = sanitizedItem.end_date || new Date().toISOString().split('T')[0];
      }
      
      // Platform-Felder validieren
      sanitizedItem.platform_name = sanitizedItem.platform_name || 'Unbekannt';
      sanitizedItem.is_ical_import = Boolean(sanitizedItem.is_ical_import);
      
      return sanitizedItem;
    });
  };

  const loadGuestData = async () => {
    if (!guest?.id) return;
    
    setLoading(true);
    try {
      const [bookingsData, invoicesData, offersData, ratingsData] = await Promise.all([
        getBookingsForGuest(guest.id),
        getInvoicesForGuest(guest.id),
        getOffersForGuest(guest.id),
        getRatingsForGuest(guest.id)
      ]);
      
      // Sanitize und validiere alle Daten
      const sanitizedBookings = sanitizeTableData(Array.isArray(bookingsData) ? bookingsData : [], 'Buchungen');
      const sanitizedInvoices = sanitizeTableData(Array.isArray(invoicesData) ? invoicesData : [], 'Rechnungen');
      const sanitizedOffers = sanitizeTableData(Array.isArray(offersData) ? offersData : [], 'Angebote');
      const sanitizedRatings = sanitizeTableData(Array.isArray(ratingsData) ? ratingsData : [], 'Bewertungen');
      
      setBookings(sanitizedBookings);
      setInvoices(sanitizedInvoices);
      setOffers(sanitizedOffers);
      setRatings(sanitizedRatings);
      
      console.log('📊 Gästedaten sanitized und geladen:', {
        bookings: sanitizedBookings.length,
        invoices: sanitizedInvoices.length,
        offers: sanitizedOffers.length,
        ratings: sanitizedRatings.length
      });
      
      // Debug: Prüfe auf Fehler-Datensätze
      const errorBookings = sanitizedBookings.filter(b => b.error);
      const errorInvoices = sanitizedInvoices.filter(i => i.error);
      const errorOffers = sanitizedOffers.filter(o => o.error);
      const errorRatings = sanitizedRatings.filter(r => r.error);
      
      if (errorBookings.length > 0 || errorInvoices.length > 0 || errorOffers.length > 0 || errorRatings.length > 0) {
        console.warn('⚠️ Fehlerhafte Datensätze gefunden:', {
          errorBookings: errorBookings.length,
          errorInvoices: errorInvoices.length,
          errorOffers: errorOffers.length,
          errorRatings: errorRatings.length
        });
      }
    } catch (error) {
      console.error('Fehler beim Laden der Gästedaten:', error);
      // Fallback auf leere Arrays
      setBookings([]);
      setInvoices([]);
      setOffers([]);
      setRatings([]);
    } finally {
      setLoading(false);
    }
  };

  // Error-Safe Render-Funktion für robuste Column-Definitionen
  const safeRender = (renderFn: Function, fallback: any = '-') => {
    return (...args: any[]) => {
      try {
        return renderFn(...args);
      } catch (error) {
        console.warn('⚠️ Column render error:', error);
        return <Text type="danger">Fehler beim Anzeigen</Text>;
      }
    };
  };

  const bookingColumns = [
    {
      title: "Unterkunft",
      key: "advert",
      render: safeRender((_: any, record: IBooking) => {
        const advertTitle = record?.advert?.title || record?.title || 'Unbekannte Unterkunft';
        if (record?.error) {
          return <Text type="danger">Daten-Fehler</Text>;
        }
        return <Text strong>{advertTitle}</Text>;
      })
    },
    {
      title: "Zeitraum",
      key: "period",
      render: safeRender((_: any, record: IBooking) => {
        if (record?.error) {
          return <Text type="danger">Daten-Fehler</Text>;
        }
        const startDate = record.start_date ? dayjs(record.start_date).format('DD.MM.YYYY') : 'Unbekannt';
        const endDate = record.end_date ? dayjs(record.end_date).format('DD.MM.YYYY') : 'Unbekannt';
        const nights = record.nights || 0;
        
        return (
          <Space direction="vertical" size={0}>
            <Text>{startDate} - {endDate}</Text>
            <Text type="secondary">{nights} Nächte</Text>
          </Space>
        );
      })
    },
    {
      title: "Buchungspersonen",
      key: "booking_persons",
      width: 140,
      render: safeRender((_: any, record: IBooking) => {
        if (record?.error) {
          return <Text type="danger">Daten-Fehler</Text>;
        }
        
        const adults = record.adults || 0;
        const children = record.children || 0;
        const dogs = record.dogs || 0;
        
        return (
          <Space direction="vertical" size={0}>
            <div>
              <Text strong>{adults}</Text> Erwachsene
            </div>
            {children > 0 && (
              <div>
                <Text strong>{children}</Text> Kinder
              </div>
            )}
            {dogs > 0 && (
              <div>
                <Text strong>{dogs}</Text> Hunde
              </div>
            )}
            <Text type="secondary" style={{ fontSize: '11px' }}>
              Gesamt: {adults + children} Personen
            </Text>
          </Space>
        );
      })
    },
    {
      title: "Platform",
      key: "platform",
      width: 130,
      render: safeRender((_: any, record: IBooking) => {
        if (record?.error) {
          return <Text type="danger">Daten-Fehler</Text>;
        }
        
        const platform = record.platform_name || 'Direkte Buchung';
        const isIcal = Boolean(record.is_ical_import);
        
        // Platform-spezifische Farben
        const platformColors = {
          'Booking.com': '#003580',
          'Airbnb': '#FF5A5F',
          'VRBO': '#0066CC',
          'Expedia': '#FFC72C',
          'FeWo-direkt': '#E6007E',
          'HRS': '#C41E3A',
          'Direkte Buchung': '#52c41a'
        };
        
        const color = platformColors[platform as keyof typeof platformColors] || '#1890ff';
        
        return (
          <Space direction="vertical" size={0}>
            <Tag 
              style={{ 
                backgroundColor: color, 
                borderColor: color,
                color: 'white',
                fontSize: '11px'
              }}
            >
              {platform}
            </Tag>
            {isIcal && (
              <Text type="secondary" style={{ fontSize: '10px' }}>
                📅 iCal Import
              </Text>
            )}
          </Space>
        );
      })
    },
    {
      title: "Betrag",
      dataIndex: "total_price",
      key: "total_price",
      width: 100,
      align: 'right' as const,
      render: safeRender((value: number, record: IBooking) => {
        if (record?.error) {
          return <Text type="danger">Fehler</Text>;
        }
        
        const safeValue = typeof value === 'number' && !isNaN(value) ? value : null;
        
        return (
          <Space direction="vertical" size={0} style={{ textAlign: 'right' }}>
            <Text strong style={{ color: '#1890ff' }}>
              {safeValue ? `€ ${safeValue.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}
            </Text>
            <Text type="secondary" style={{ fontSize: '10px' }}>
              Gesamtbetrag
            </Text>
          </Space>
        );
      })
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: safeRender((value: string | number, record: IBooking) => {
        if (record?.error) {
          return <Tag color="red">Fehler</Tag>;
        }
        
        // Status-Mapping für numerische Codes
        const statusMapping = {
          1: { text: 'Nicht bearbeitet', color: 'orange', description: 'Buchung wurde noch nicht bearbeitet' },
          2: { text: 'Angenommen', color: 'green', description: 'Buchung wurde angenommen und bestätigt' },
          3: { text: 'Abgelehnt', color: 'red', description: 'Buchung wurde abgelehnt' },
          4: { text: 'Storniert', color: 'volcano', description: 'Buchung wurde storniert' },
          5: { text: 'Extern', color: 'blue', description: 'Externe Buchung (z.B. über Booking-Plattformen)' },
          6: { text: 'Intern', color: 'purple', description: 'Interne Buchung (direkt erstellt)' }
        };

        // Fallback für Text-Status
        const textColors = {
          'pending': 'orange',
          'confirmed': 'green', 
          'cancelled': 'red',
          'completed': 'blue',
          'unedited': 'orange',
          'accepted': 'green',
          'rejected': 'red',
          'external': 'blue',
          'internal': 'purple',
          'error': 'red'
        };

        // Sichere Werte-Behandlung
        const safeValue = value ?? 'unknown';
        
        // Numerischer Status
        if (typeof safeValue === 'number' || (!isNaN(Number(safeValue)) && safeValue !== '')) {
          const numericValue = Number(safeValue);
          const statusInfo = statusMapping[numericValue as keyof typeof statusMapping];
          
          if (statusInfo) {
            return (
              <Tooltip title={`Status ${numericValue}: ${statusInfo.description}`}>
                <Tag color={statusInfo.color}>
                  {statusInfo.text} ({numericValue})
                </Tag>
              </Tooltip>
            );
          }
        }

        // Text-Status (Fallback)
        const color = textColors[safeValue as keyof typeof textColors] || 'default';
        return (
          <Tooltip title={`Status: ${safeValue}`}>
            <Tag color={color}>{safeValue}</Tag>
          </Tooltip>
        );
      })
    }
  ];

  const documentColumns = [
    {
      title: "Nummer",
      dataIndex: "number",
      key: "number",
      render: (value: string) => <Text strong>{value}</Text>
    },
    {
      title: "Datum",
      dataIndex: "date",
      key: "date",
      render: (value: string) => dayjs(value).format('DD.MM.YYYY')
    },
    {
      title: "Betrag",
      dataIndex: "total_amount",
      key: "total_amount",
      render: (value: number) => <Text strong>€ {value?.toFixed(2)}</Text>
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value: string) => {
        const statusDescriptions = {
          'pending': 'Dokument ist ausstehend und wartet auf Bearbeitung',
          'confirmed': 'Dokument wurde bestätigt',
          'cancelled': 'Dokument wurde storniert',
          'paid': 'Rechnung wurde bezahlt'
        };
        
        const colors = {
          pending: 'orange',
          confirmed: 'green',
          cancelled: 'red',
          paid: 'blue'
        };
        
        const color = colors[value as keyof typeof colors] || 'default';
        const description = statusDescriptions[value as keyof typeof statusDescriptions] || `Status: ${value}`;
        
        return (
          <Tooltip title={description}>
            <Tag color={color}>{value}</Tag>
          </Tooltip>
        );
      }
    },
    {
      title: "Aktionen",
      key: "actions",
      render: (_: any, record: IDocument) => (
        <Button 
          size="small" 
          icon={<DownloadOutlined />}
          onClick={() => downloadInvoicePDF(record.id)}
        >
          PDF
        </Button>
      )
    }
  ];

  if (!guest) {
    return <div>Lade Gästedaten...</div>;
  }

  // Berechne Statistiken
  const totalSpent = invoices.reduce((sum, invoice) => sum + (invoice.total_amount || 0), 0);
  const completedBookings = bookings.filter(b => b.status === 'completed').length;
  const lastBooking = bookings.sort((a, b) => 
    dayjs(b.end_date).unix() - dayjs(a.end_date).unix()
  )[0];

  return (
    <Show
      headerButtons={[
        <Button 
          key="list" 
          type="default"
          icon={<UnorderedListOutlined />}
          onClick={() => list("guests")}
        >
          Gästedaten
        </Button>,
        <Button 
          key="edit" 
          type="primary"
          icon={<EditOutlined />}
          onClick={() => edit("guests", guest?.id)}
        >
          Bearbeiten
        </Button>,
        <Button 
          key="refresh" 
          type="default"
          icon={<ReloadOutlined />}
          onClick={() => window.location.reload()}
        >
          Aktualisieren
        </Button>
      ]}
    >
      <Row gutter={[24, 24]}>
        {/* Gast-Informationen */}
        <Col span={24}>
          <Card>
            <Row gutter={24}>
              <Col span={4}>
                <Avatar size={80} icon={<UserOutlined />} />
              </Col>
              <Col span={20}>
                <Space direction="vertical" size={8}>
                  <Title level={2} style={{ margin: 0 }}>{guest.name}</Title>
                  <Space size={16} wrap>
                    {guest.email && (
                      <Space>
                        <MailOutlined />
                        <Text>{guest.email}</Text>
                      </Space>
                    )}
                    {guest.mobile && (
                      <Space>
                        <PhoneOutlined />
                        <Text>{guest.mobile}</Text>
                      </Space>
                    )}
                    {(guest.street || guest.housnr) && (
                      <Space>
                        <HomeOutlined />
                        <Text>{guest.street || ''}{guest.street && guest.housnr ? ' ' : ''}{guest.housnr || ''}</Text>
                      </Space>
                    )}
                    {(guest.city || guest.country) && (
                      <Space>
                        <HomeOutlined style={{ opacity: 0.6 }} />
                        <Text type="secondary">{guest.zip || ''}{guest.zip && (guest.city || guest.country) ? ' ' : ''}{guest.city || ''}{guest.city && guest.country ? ', ' : ''}{guest.country || ''}</Text>
                      </Space>
                    )}
                    {guest.company && (
                      <Space>
                        <Text type="secondary">
                          <strong>{guest.company}</strong>
                          {guest.company_tax_id && ` (USt-ID: ${guest.company_tax_id})`}
                        </Text>
                      </Space>
                    )}
                  </Space>
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Statistiken */}
        <Col span={24}>
          <Row gutter={16}>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Buchungen gesamt"
                  value={guest.total_bookings || 0}
                  prefix={<CalendarOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Gesamtumsatz"
                  value={totalSpent}
                  precision={2}
                  suffix="€"
                  prefix={<EuroOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Abgeschlossene Buchungen"
                  value={completedBookings}
                  prefix={<FileTextOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Ø Buchungswert"
                  value={bookings.length > 0 ? totalSpent / bookings.length : 0}
                  precision={2}
                  suffix="€"
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </Col>
          </Row>
        </Col>

        {/* Details und Aktivitäten */}
        <Col span={24}>
          <Tabs defaultActiveKey="details">
            <TabPane tab="Details" key="details">
              <Row gutter={24}>
                <Col span={12}>
                  <Space direction="vertical" style={{ width: '100%' }} size={16}>
                    <Card title="Persönliche Daten" size="small">
                      <Descriptions column={1} size="small">
                        <Descriptions.Item label="Vollständiger Name">
                          {guest.firstname || guest.name?.split(' ')[0] || ''} {guest.lastname || guest.name?.split(' ').slice(1).join(' ') || ''}
                        </Descriptions.Item>
                        <Descriptions.Item label="E-Mail">
                          {guest.email || 'Nicht angegeben'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Telefon">
                          {guest.mobile || 'Nicht angegeben'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Adresse">
                          {guest.street && guest.housnr ? `${guest.street} ${guest.housnr}` : guest.street || 'Nicht angegeben'}
                        </Descriptions.Item>
                        <Descriptions.Item label="PLZ / Ort">
                          {guest.zip || ''} {guest.city || 'Nicht angegeben'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Land">
                          {guest.country || 'Deutschland'}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                    
                    {/* Firmendaten anzeigen falls vorhanden */}
                    {guest.company && (
                      <Card title="Firmendaten" size="small">
                        <Descriptions column={1} size="small">
                          <Descriptions.Item label="Firma">
                            {guest.company}
                          </Descriptions.Item>
                          {guest.company_email && (
                            <Descriptions.Item label="Firmen-E-Mail">
                              {guest.company_email}
                            </Descriptions.Item>
                          )}
                          {guest.company_phone && (
                            <Descriptions.Item label="Firmentelefon">
                              {guest.company_phone}
                            </Descriptions.Item>
                          )}
                          {guest.company_address && (
                            <Descriptions.Item label="Firmenadresse">
                              {guest.company_address}
                            </Descriptions.Item>
                          )}
                          {(guest.company_zip || guest.company_city) && (
                            <Descriptions.Item label="Firmen PLZ / Ort">
                              {guest.company_zip || ''} {guest.company_city || ''}
                            </Descriptions.Item>
                          )}
                          {guest.company_country && (
                            <Descriptions.Item label="Firmenland">
                              {guest.company_country}
                            </Descriptions.Item>
                          )}
                          {guest.company_tax_id && (
                            <Descriptions.Item label="USt-IdNr.">
                              {guest.company_tax_id}
                            </Descriptions.Item>
                          )}
                        </Descriptions>
                      </Card>
                    )}
                  </Space>
                </Col>
                <Col span={12}>
                  <Card title="Buchungshistorie" size="small">
                    <Timeline>
                      {bookings.slice(0, 5).map((booking) => (
                        <Timeline.Item
                          key={booking.id}
                          color={booking.status === 'completed' ? 'green' : 'blue'}
                        >
                          <Space direction="vertical" size={0}>
                            <Text strong>{booking.advert?.title}</Text>
                            <Text type="secondary">
                              {dayjs(booking.start_date).format('DD.MM.YYYY')} - {dayjs(booking.end_date).format('DD.MM.YYYY')} ({booking.nights} Nächte)
                            </Text>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Text strong style={{ color: '#1890ff' }}>
                                {booking.total_price ? `€ ${booking.total_price.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}
                              </Text>
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                {booking.adults} Erw.{booking.children > 0 && `, ${booking.children} Kinder`}{booking.dogs && booking.dogs > 0 && `, ${booking.dogs} Hunde`}
                              </Text>
                            </div>
                          </Space>
                        </Timeline.Item>
                      ))}
                    </Timeline>
                  </Card>
                </Col>
              </Row>
            </TabPane>

            <TabPane tab={`Buchungen (${Array.isArray(bookings) ? bookings.length : 0})`} key="bookings">
              {Array.isArray(bookings) && bookings.length > 0 ? (
                <Table
                  dataSource={bookings}
                  columns={bookingColumns}
                  rowKey={(record) => record?.id || `error-${Math.random()}`}
                  size="small"
                  loading={loading}
                  pagination={{ pageSize: 10 }}
                  locale={{
                    emptyText: loading ? 'Laden...' : 'Keine Buchungen vorhanden'
                  }}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  {loading ? (
                    <Text type="secondary">Buchungen werden geladen...</Text>
                  ) : (
                    <Text type="secondary">Keine Buchungen für diesen Gast vorhanden.</Text>
                  )}
                </div>
              )}
            </TabPane>

            <TabPane tab={`Rechnungen (${Array.isArray(invoices) ? invoices.length : 0})`} key="invoices">
              <Table
                dataSource={Array.isArray(invoices) ? invoices : []}
                columns={documentColumns}
                rowKey="id"
                size="small"
                loading={loading}
                pagination={{ pageSize: 10 }}
              />
            </TabPane>

            <TabPane tab={`Angebote (${Array.isArray(offers) ? offers.length : 0})`} key="offers">
              <Table
                dataSource={Array.isArray(offers) ? offers : []}
                columns={documentColumns}
                rowKey="id"
                size="small"
                loading={loading}
                pagination={{ pageSize: 10 }}
              />
            </TabPane>

            <TabPane tab={`Bewertungen (${Array.isArray(ratings) ? ratings.length : 0})`} key="ratings">
              {Array.isArray(ratings) && ratings.length > 0 ? (
                <Table
                  dataSource={Array.isArray(ratings) ? ratings : []}
                  columns={[
                    {
                      title: "Gast",
                      dataIndex: "guest_name",
                      key: "guest_name",
                      render: (value: string) => <Text strong>{value || 'Anonym'}</Text>
                    },
                    {
                      title: "Datum",
                      dataIndex: "date",
                      key: "date",
                      render: (value: string) => dayjs(value).format('DD.MM.YYYY')
                    },
                    {
                      title: "Bewertung",
                      key: "rating",
                      width: 200,
                      render: (_: any, record: any) => (
                        <Space direction="vertical" size={0}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Text strong>{record.rating}</Text>
                            <span style={{ color: '#faad14', fontSize: '16px' }}>
                              {'★'.repeat(Math.floor(record.rating))}{'☆'.repeat(5 - Math.floor(record.rating))}
                            </span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#666' }}>
                            Aufenthalt: {record.stay}/5, Sauberkeit: {record.cleanliness}/5, 
                            Service: {record.service}/5, Angaben: {record.specifications}/5
                          </div>
                        </Space>
                      )
                    },
                    {
                      title: "Kommentar",
                      dataIndex: "comment",
                      key: "comment",
                      ellipsis: true,
                      width: '35%',
                      render: (value: string, record: any) => (
                        <Space direction="vertical" size={4}>
                          <Text>{value || 'Kein Kommentar'}</Text>
                          {record.recommendation && (
                            <Tag color="green">Empfohlen</Tag>
                          )}
                        </Space>
                      )
                    },
                    {
                      title: "Status",
                      dataIndex: "status",
                      key: "status",
                      render: (value: number) => {
                        const statusMap = {
                          0: { text: 'Neu', color: 'blue' },
                          1: { text: 'Gelesen', color: 'orange' },
                          2: { text: 'Beantwortet', color: 'green' },
                          3: { text: 'Gemeldet', color: 'red' }
                        };
                        
                        const status = statusMap[value as keyof typeof statusMap] || { text: 'Unbekannt', color: 'default' };
                        
                        return (
                          <Tooltip title={`Bewertungsstatus: ${status.text}`}>
                            <Tag color={status.color}>{status.text}</Tag>
                          </Tooltip>
                        );
                      }
                    }
                  ]}
                  rowKey="id"
                  size="small"
                  loading={loading}
                  pagination={{ pageSize: 10 }}
                  expandable={{
                    expandedRowRender: (record) => (
                      <div style={{ margin: 0 }}>
                        <Row gutter={16}>
                          <Col span={12}>
                            <Descriptions title="Detailbewertung" size="small">
                              <Descriptions.Item label="Aufenthalt">{record.stay}/5 ⭐</Descriptions.Item>
                              <Descriptions.Item label="Sauberkeit">{record.cleanliness}/5 ⭐</Descriptions.Item>
                              <Descriptions.Item label="Service">{record.service}/5 ⭐</Descriptions.Item>
                              <Descriptions.Item label="Angaben stimmen">{record.specifications}/5 ⭐</Descriptions.Item>
                            </Descriptions>
                          </Col>
                          <Col span={12}>
                            {record.reply && (
                              <Card size="small" title="Antwort" style={{ marginTop: 8 }}>
                                <Text>{record.reply}</Text>
                                {record.reply_date && (
                                  <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                                    Beantwortet am: {dayjs(record.reply_date).format('DD.MM.YYYY')}
                                  </div>
                                )}
                              </Card>
                            )}
                          </Col>
                        </Row>
                      </div>
                    ),
                    rowExpandable: (record) => record.reply || true,
                  }}
                />
              ) : (
                <Card>
                  <Text type="secondary">Noch keine Bewertungen vorhanden.</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Bewertungen werden über das v20_ratings System verwaltet und müssen über eingeladene Benutzer erstellt werden.
                  </Text>
                </Card>
              )}
            </TabPane>

            <TabPane tab="Notizen" key="notes">
              <Card>
                <Text style={{ whiteSpace: 'pre-wrap' }}>
                  {guest.notes || 'Keine Notizen vorhanden.'}
                </Text>
              </Card>
            </TabPane>
          </Tabs>
        </Col>
      </Row>
    </Show>
  );
}; 